import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { Author, ExistingLocation, PositionDTO, Signalement } from '../api/signalement'
import { getInitialSignalement, getPositionTypeLabel } from '../utils/signalement.utils'
import { IBANPlateformeResult } from '../api/ban-plateforme/types'
import { ChangesRequested } from '../types/signalement.types'
import { getValueFromLocalStorage, LocalStorageKeys } from '../utils/localStorage.utils'

export interface SignalementContextValue {
  signalement: Signalement | null
  createSignalement: (
    signalementType: Signalement.type,
    adresse: IBANPlateformeResult,
    changesRequested?: Partial<ChangesRequested>,
    creationType?: ExistingLocation.type,
  ) => void
  deleteSignalement: () => void
  onEditSignalement: (property: keyof Signalement, key?: string) => (value: any) => void
  hasSignalementChanged: boolean
}

export const SignalementContext = createContext<SignalementContextValue>({
  signalement: null,
  createSignalement: () => {},
  deleteSignalement: () => {},
  onEditSignalement: () => () => {},
  hasSignalementChanged: false,
})

interface SignalementContextProviderProps {
  children: React.ReactNode
}

export function SignalementContextProvider(props: Readonly<SignalementContextProviderProps>) {
  const [initialSignalement, setInitialSignalement] = useState<Signalement | null>(null)
  const [signalement, setSignalement] = useState<Signalement | null>(null)
  const location = useLocation()
  const navigationType = useNavigationType()
  const previousLocationKey = useRef(location.key)

  useEffect(() => {
    if (previousLocationKey.current === location.key) {
      return
    }

    previousLocationKey.current = location.key
    if (navigationType === 'POP' && signalement) {
      setInitialSignalement(null)
      setSignalement(null)
    }
  }, [location.key, navigationType, signalement])

  const onEditSignalement = useCallback(
    (property: keyof Signalement, key?: string) => (value: any) => {
      if (key) {
        setSignalement(
          (state) =>
            state &&
            ({
              ...state,
              [property]: {
                ...(state[property] as object),
                [key]: value,
              },
            } as Signalement),
        )
      } else {
        setSignalement((state) => state && ({ ...state, [property]: value } as Signalement))
      }
    },
    [setSignalement],
  )

  const createSignalement = useCallback(
    (
      signalementType: Signalement.type,
      adresse: IBANPlateformeResult,
      changesRequested?: Partial<ChangesRequested>,
      creationType?: ExistingLocation.type,
    ) => {
      const signalement = getInitialSignalement(adresse, signalementType, creationType)
      setInitialSignalement(signalement)
      setSignalement(signalement)

      // Set the changesRequested if provided in query params
      for (const [key, value] of Object.entries(changesRequested ?? {})) {
        if (key === 'positions') {
          const positions = (value as PositionDTO[]).map(({ point, type }) => ({
            point,
            type: getPositionTypeLabel(type) ? type : PositionDTO.type.ENTR_E,
          }))
          onEditSignalement('changesRequested', key)(positions)
        } else {
          onEditSignalement('changesRequested', key)(value)
        }
      }

      // Set author if provided in local storage
      const authorContact = getValueFromLocalStorage<Author>(LocalStorageKeys.AUTHOR_CONTACT)
      if (authorContact) {
        onEditSignalement('author', 'firstName')(authorContact.firstName)
        onEditSignalement('author', 'lastName')(authorContact.lastName)
        onEditSignalement('author', 'email')(authorContact.email)
      }
    },
    [setSignalement],
  )

  const deleteSignalement = useCallback(() => {
    setInitialSignalement(null)
    setSignalement(null)
  }, [setSignalement])

  const hasSignalementChanged = useMemo(() => {
    return (
      JSON.stringify(initialSignalement?.changesRequested) !==
      JSON.stringify(signalement?.changesRequested, (_key, value) => {
        if (_key === 'comment') {
          return ''
        }
        if (_key === 'numero') {
          return parseInt(value, 10)
        }
        if (typeof value === 'string') {
          return value.trim()
        }
        return value
      })
    )
  }, [initialSignalement, signalement])

  const value = useMemo(
    () => ({
      createSignalement,
      deleteSignalement,
      onEditSignalement,
      hasSignalementChanged,
      signalement,
    }),
    [createSignalement, deleteSignalement, onEditSignalement, hasSignalementChanged, signalement],
  )

  return <SignalementContext.Provider value={value} {...props} />
}
